export type Role = 'student' | 'employee' | 'guardian';

// Role words for the current UI language, carried on each import batch.
export type RoleTranslations = {
  student: string;
  employee: string;
  guardian: string;
};

// Keyed to match `excel_upload_<name>.xlsx`, so a row can be checked against the role dropdown of
// the template it describes. excelTemplateRoles.test.ts does exactly that.
export const TYPE_TO_ROLE_BY_TEMPLATE: Readonly<Record<string, Readonly<Record<string, Role>>>> = {
  // No dropdown; these are the words the guide and older revisions use.
  english: { student: 'student', teacher: 'employee', staff: 'employee', employee: 'employee', guardian: 'guardian' },
  sweden: { elev: 'student', lärare: 'employee', personal: 'employee', vårdnadshavare: 'guardian' },
  germany: {
    'schüler:in': 'student',
    schüler: 'student', // older template revisions used this form
    personal: 'employee',
    erziehungsberechtigte: 'guardian',
  },
  finland: { oppilas: 'student', opettaja: 'employee', henkilökunta: 'employee', huoltaja: 'guardian' },
  france: { élève: 'student', enseignant: 'employee', soignant: 'guardian' },
  norway: { elev: 'student', personale: 'employee', foresatt: 'guardian' },
  dutch: { leerling: 'student', personeel: 'employee', docent: 'employee', voogd: 'guardian' },
};

// Flat, because a sheet is not tied to the realm it is uploaded in: any template's words are
// accepted. A word repeated across templates must mean the same role - roles.test.ts checks that,
// since the last row declared would otherwise win silently. A Map, not an object, because it is
// looked up with whatever the customer typed.
const TYPE_TO_ROLE: ReadonlyMap<string, Role> = new Map(
  Object.values(TYPE_TO_ROLE_BY_TEMPLATE).flatMap((templateWords) => Object.entries(templateWords)),
);

export const resolveRole = (type: string, translations?: RoleTranslations): Role | null => {
  const theType = type.trim().toLowerCase();
  const fromTemplate = TYPE_TO_ROLE.get(theType);
  if (fromTemplate) return fromTemplate;
  // Wording no template covers
  if (translations) {
    if (theType === translations.student.trim().toLowerCase()) return 'student';
    if (theType === translations.employee.trim().toLowerCase()) return 'employee';
    if (theType === translations.guardian.trim().toLowerCase()) return 'guardian';
  }
  return null;
};
